import { ListChecks, ListFilter } from "lucide-react";
import type { RowFilterSpec } from "@/generated/types.gen";
import { sqlOneLine } from "@/utils/sqlOneLine";
import { isRecord } from "@/utils/isRecord";
import type { ChangedBy } from "@/components/TableTool/gridState";
import type { SortDir } from "@/components/TableTool/TableGrid";
import {
  OPERATORS,
  getOpsForType,
  type FieldType,
} from "@/components/TableTool/filter/filterTypes";
import { isSqlQuery } from "@/components/TableTool/filter/sqlCompletion";
import { type GenieTool } from "../registries/tool-registry";
import { useGenieContext } from "../use-genie-context";
import { useGenieTools } from "../use-genie-tools";
import { useLiveDeps } from "../use-live-deps";

export interface TableViewFilter {
  field: string;
  op: string;
  value: string | number;
  value2?: number;
}

export interface TableViewSort {
  column: string;
  direction: SortDir;
}

export interface TableViewChange {
  filters?: TableViewFilter[];
  search?: string;
  sql?: string | null;
  sort?: TableViewSort | null;
  page?: number;
  pageSize?: number;
}

// A filter as table_set_view takes it, so Genie can send the user's back.
export interface TableViewStateFilter {
  column: string;
  op: string;
  value: RowFilterSpec["value"];
  value2?: RowFilterSpec["value2"];
}

export interface TableViewState {
  filters: TableViewStateFilter[];
  search: string;
  sql: string | null;
  sort: TableViewSort | null;
  page: number;
  pageSize: number;
  selectedCount: number;
  // Up to the first 50 picked rows; empty while every matching row is selected.
  selectedIds: string[];
  // Rows carved out of an all-matching selection.
  excludedIds: string[];
  allMatchingSelected: boolean;
  // Who changed the view last: a user's own change since Genie's must stand.
  lastChangedBy: ChangedBy | null;
}

export type TableSelection = { rowIds: string[] } | { allMatching: true };

export interface TableViewDeps {
  tableName: string;
  sqlName: string;
  columns: { name: string; kind: FieldType }[];
  // False where the grid has no selection (a read-only table).
  canSelect: boolean;
  // The grid as of its latest change, before React renders it: Genie can
  // change the view twice, or change it and select, in one step.
  current: () => { sql: string | null; pageSize: number; version: number };
  pageSizes: number[];
  setView: (change: TableViewChange) => void;
  // How many rows the view would show once changed.
  countRows: (change: TableViewChange) => Promise<number>;
  // Resolves to how many rows end up selected; null clears the selection.
  // Rejects, with the reason, a selection the view cannot hold.
  selectRows: (selection: TableSelection | null) => Promise<number>;
}

type Read<T> = { ok: T } | { error: string };

const asError = (e: unknown) => ({
  error: e instanceof Error ? e.message : String(e),
});

function readFilters(
  raw: unknown,
  columns: TableViewDeps["columns"]
): Read<TableViewFilter[]> {
  if (!Array.isArray(raw)) return { error: "filters must be a list." };
  const filters: TableViewFilter[] = [];
  for (const item of raw) {
    if (!isRecord(item)) {
      return { error: "Each filter is an object: {column, op, value}." };
    }
    const column = columns.find((c) => c.name === item.column);
    if (!column) {
      return {
        error: `No column "${String(item.column)}" on this table. Its columns: ${columns
          .map((c) => c.name)
          .join(", ")}.`,
      };
    }
    const ops = getOpsForType(column.kind).map((o) => o.id);
    if (typeof item.op !== "string" || !ops.includes(item.op)) {
      return {
        error: `"${String(item.op)}" does not apply to ${column.name}. Use one of: ${ops.join(", ")}.`,
      };
    }
    if (typeof item.value !== "string" && typeof item.value !== "number") {
      return { error: `Give ${column.name}'s filter a value.` };
    }
    if (item.op === "between" && typeof item.value2 !== "number") {
      return { error: "between needs value2, the upper bound." };
    }
    filters.push({
      field: column.name,
      op: item.op,
      value: item.value,
      ...(typeof item.value2 === "number" ? { value2: item.value2 } : {}),
    });
  }
  return { ok: filters };
}

function readChange(
  input: Record<string, unknown>,
  deps: TableViewDeps
): Read<TableViewChange> {
  const change: TableViewChange = {};
  if (input.filters !== undefined) {
    const filters = readFilters(input.filters, deps.columns);
    if ("error" in filters) return filters;
    change.filters = filters.ok;
  }
  if (input.search !== undefined) {
    change.search = input.search === null ? "" : String(input.search);
  }
  if (input.sql !== undefined) {
    if (input.sql !== null && !isSqlQuery(String(input.sql))) {
      return {
        error:
          "sql shows a query's result as the grid: give one SELECT or WITH statement.",
      };
    }
    change.sql = input.sql === null ? null : sqlOneLine(String(input.sql));
  }
  if (input.sort !== undefined) {
    const sort = input.sort;
    if (sort === null) change.sort = null;
    else if (!isRecord(sort)) {
      return { error: "sort is {column, direction}, or null to clear it." };
    } else if (!deps.columns.some((c) => c.name === sort.column)) {
      return { error: `No column "${String(sort.column)}" to sort by.` };
    } else {
      change.sort = {
        column: String(sort.column),
        direction: sort.direction === "desc" ? "desc" : "asc",
      };
    }
  }
  if (input.page !== undefined) change.page = Math.max(1, Number(input.page));
  if (input.page_size !== undefined) {
    if (!deps.pageSizes.includes(Number(input.page_size))) {
      return { error: `page_size is one of ${deps.pageSizes.join(", ")}.` };
    }
    change.pageSize = Number(input.page_size);
  }
  if (Object.keys(change).length === 0) {
    return {
      error:
        "Nothing to change: pass filters, search, sql, sort, page or page_size.",
    };
  }
  // A SQL view ignores filters, search and sort: changing them leaves it.
  const redefinesRows =
    change.filters !== undefined ||
    change.search !== undefined ||
    change.sort !== undefined;
  if (deps.current().sql && change.sql === undefined && redefinesRows) {
    change.sql = null;
  }
  // A query's total comes with its first page, so a new one starts there.
  if (change.sql) delete change.page;
  return { ok: change };
}

function describeChange(input: Record<string, unknown>): string {
  const filters = Array.isArray(input.filters)
    ? input.filters.filter(isRecord)
    : null;
  if (filters?.length) {
    return `Filtered to ${filters
      .map((f) => `${f.column} ${String(f.op).replace("_", " ")} ${f.value}`)
      .join(", ")}`;
  }
  if (typeof input.sql === "string") return "Showed a query's result";
  if (typeof input.search === "string" && input.search)
    return `Searched for ${input.search}`;
  if (isRecord(input.sort)) return `Sorted by ${String(input.sort.column)}`;
  return "Changed the table view";
}

export function buildTableViewTools(deps: TableViewDeps): GenieTool[] {
  const columnNames = deps.columns.map((c) => c.name);
  // Genie can change the view and select in one step, and the chat runs both
  // calls at once: run them in the order they were made, so a selection
  // applies to the view just set.
  let queue: Promise<unknown> = Promise.resolve();
  const inOrder = <T>(run: () => Promise<T>): Promise<T> => {
    const done = queue.then(run, run);
    queue = done.catch(() => undefined);
    return done;
  };
  // The latest view change Genie asked for, and the grid's version when it
  // did. While it failed and nobody has changed the view since, the grid
  // still shows the view before it, so an all-matching selection is refused:
  // it would cover rows the user did not ask for.
  let lastView: {
    result: Promise<Record<string, unknown>>;
    version: () => number;
  } | null = null;
  const tools: GenieTool[] = [
    {
      name: "table_set_view",
      description:
        `Change what the grid on screen shows for the table ${deps.tableName} (sql_name ${deps.sqlName}): ` +
        "filters (the list replaces every filter), a text search, a SELECT shown as the grid, sort, page and page size. " +
        "When the user asks to see, find, filter, sort, count or list rows of this table, show the answer in the grid with this tool " +
        "rather than as a table in chat: filters when they express it, sql when it needs a join or an aggregate. " +
        "To answer about the rows already on screen, read them with tables_query instead of changing the view. " +
        "Changing the view selects nothing: when the user asks to select or tick rows, call table_select_rows. " +
        "Then say in one line what the grid shows and how many rows match. " +
        "The table_view context is what the user sees now. When its lastChangedBy is user, the user changed the view after you " +
        "(removed a filter, cleared a search): that is their choice, so never put back what they removed. " +
        "Send only the fields this request changes; an omitted field keeps what is on screen. " +
        '[] , "" or null clears a field. Text columns take contains, is, is_not; ' +
        "number columns take gt, lt, gte, lte, eq, between (with value2). It changes only the view, never the data.",
      parameters: {
        type: "object",
        properties: {
          filters: {
            type: "array",
            items: {
              type: "object",
              properties: {
                column: { type: "string", enum: columnNames },
                op: {
                  type: "string",
                  enum: OPERATORS.map((o) => o.id),
                },
                value: { type: ["string", "number"] },
                value2: { type: "number" },
              },
              required: ["column", "op", "value"],
            },
          },
          search: { type: "string" },
          sql: { type: ["string", "null"] },
          sort: {
            type: ["object", "null"],
            properties: {
              column: { type: "string", enum: columnNames },
              direction: { type: "string", enum: ["asc", "desc"] },
            },
            required: ["column", "direction"],
          },
          page: { type: "integer", minimum: 1 },
          page_size: { type: "integer", enum: deps.pageSizes },
        },
      },
      execute: (input) => {
        // Read when the change runs, after any change queued before it.
        let version = -1;
        const change = async (): Promise<Record<string, unknown>> => {
          version = deps.current().version;
          const read = readChange(input, deps);
          if ("error" in read) return read;
          let matching: number;
          try {
            matching = await deps.countRows(read.ok);
          } catch (e) {
            return {
              error: `Counting the view's rows failed, so the grid is unchanged: ${e instanceof Error ? e.message : String(e)}`,
            };
          }
          const pageSize = read.ok.pageSize ?? deps.current().pageSize;
          const pages = Math.max(1, Math.ceil(matching / pageSize));
          if (read.ok.page !== undefined && read.ok.page > pages) {
            return {
              error: `The view has ${matching} rows, so ${pages} ${pages === 1 ? "page" : "pages"} of ${pageSize}: there is no page ${read.ok.page}.`,
            };
          }
          deps.setView(read.ok);
          return { matching_rows: matching };
        };
        // Malformed input never escapes as a throw: the tool answers {error}.
        const result = inOrder(() => change().catch(asError));
        lastView = { result, version: () => version };
        return result;
      },
      display: {
        icon: ListFilter,
        messageStart: () => "Changing the table view...",
        messageEnd: describeChange,
        messageError: "Could not change the table view",
      },
    },
    {
      name: "table_select_rows",
      description:
        `Select rows of the grid on screen (${deps.tableName}) so the user can act on them from the selection bar: ` +
        "row_ids (from tables_get_rows or tables_query), or all_matching for every row matching the current view. " +
        "clear empties the selection. Selecting deletes nothing; the user acts on the selection. " +
        "Filtering the grid does not select its rows: after a filter, still call this tool, and only say rows are selected once it returns.",
      parameters: {
        type: "object",
        properties: {
          row_ids: { type: "array", items: { type: "string" } },
          all_matching: { type: "boolean" },
          clear: { type: "boolean" },
        },
      },
      execute: (input) => {
        const view = lastView;
        return inOrder(async () => {
          const viewed = view ? await view.result : null;
          const stillShowing = view?.version() === deps.current().version;
          if (
            input.all_matching === true &&
            viewed &&
            "error" in viewed &&
            stillShowing
          ) {
            return {
              error: `The last view change failed, so the grid still shows the view before it and nothing was selected: ${String(viewed.error)} Set the view again, or pass row_ids.`,
            };
          }
          if (input.clear === true) {
            return { selected: await deps.selectRows(null) };
          }
          if (input.all_matching === true) {
            try {
              return {
                selected: await deps.selectRows({ allMatching: true }),
              };
            } catch (e) {
              return { error: e instanceof Error ? e.message : String(e) };
            }
          }
          if (Array.isArray(input.row_ids) && input.row_ids.length > 0) {
            return {
              selected: await deps.selectRows({
                rowIds: input.row_ids.map(String),
              }),
            };
          }
          return { error: "Pass row_ids, all_matching or clear." };
        });
      },
      display: {
        icon: ListChecks,
        messageStart: () => "Selecting rows...",
        messageEnd: () => "Selected rows",
        messageError: "Could not select rows",
      },
    },
  ];
  return deps.canSelect
    ? tools
    : tools.filter((t) => t.name !== "table_select_rows");
}

// Mounted by the table page: Genie changes and reads the grid on screen,
// while the grid is the tab showing. The tools register again whenever
// ``deps`` changes, so the caller keeps it stable: its functions read the grid
// when they run rather than closing over a render's values.
export function useTableViewTools(
  deps: TableViewDeps,
  view: TableViewState,
  enabled: boolean
): void {
  useGenieContext(
    "table_view",
    "What the grid on screen shows: its filters, search, SQL query, sort, page and selection",
    view
  );
  useGenieTools(() => buildTableViewTools(deps), [deps], enabled);
}

const LIST_SORT_COLUMNS = ["name", "columns", "rows", "updated_at"] as const;

export interface TablesListChange {
  search?: string;
  tagIds?: string[];
  sort?: {
    column: (typeof LIST_SORT_COLUMNS)[number];
    direction: SortDir;
  } | null;
}

export interface TablesListDeps {
  tags: { id: string; name: string }[];
  setView: (change: TablesListChange) => void;
  // How many tables the list would show once changed.
  countTables: (change: TablesListChange) => number;
}

export function buildTablesListTools(deps: TablesListDeps): GenieTool[] {
  return [
    {
      name: "tables_list_set_view",
      description:
        "Change what the Tables page on screen lists: search by name, filter by tags (names; [] clears) " +
        "and sort by name, columns, rows or updated_at. Use it when the user asks to see or find tables while on this page, " +
        `then say how many tables it lists. This workspace's tags: ${deps.tags.map((t) => t.name).join(", ") || "none"}; ` +
        "a word that names one of them filters by that tag, not by name. It changes only the list, never a table.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          // An empty enum is not a valid schema: with no tags there is none.
          ...(deps.tags.length > 0
            ? {
                tags: {
                  type: "array",
                  items: { type: "string", enum: deps.tags.map((t) => t.name) },
                },
              }
            : {}),
          sort: {
            type: ["object", "null"],
            properties: {
              column: { type: "string", enum: [...LIST_SORT_COLUMNS] },
              direction: { type: "string", enum: ["asc", "desc"] },
            },
            required: ["column", "direction"],
          },
        },
      },
      execute: async (input) => {
        const change: TablesListChange = {};
        if (input.search !== undefined) {
          change.search = input.search === null ? "" : String(input.search);
        }
        if (Array.isArray(input.tags)) {
          const ids: string[] = [];
          for (const name of input.tags.map(String)) {
            const tag = deps.tags.find(
              (t) => t.name.toLowerCase() === name.trim().toLowerCase()
            );
            if (!tag) {
              return {
                error: deps.tags.length
                  ? `No tag "${name}" in this workspace. Its tags: ${deps.tags
                      .map((t) => t.name)
                      .join(", ")}.`
                  : `No tag "${name}": this workspace has no tags.`,
              };
            }
            ids.push(tag.id);
          }
          change.tagIds = ids;
        }
        if (input.sort !== undefined) {
          const sort = input.sort;
          if (sort === null) change.sort = null;
          else if (!isRecord(sort)) {
            return {
              error: "sort is {column, direction}, or null to clear it.",
            };
          } else {
            const column = LIST_SORT_COLUMNS.find((c) => c === sort.column);
            if (!column) {
              return {
                error: `Sort by one of ${LIST_SORT_COLUMNS.join(", ")}.`,
              };
            }
            change.sort = {
              column,
              direction: sort.direction === "desc" ? "desc" : "asc",
            };
          }
        }
        if (Object.keys(change).length === 0) {
          return { error: "Nothing to change: pass search, tags or sort." };
        }
        deps.setView(change);
        return { matching_tables: deps.countTables(change) };
      },
      display: {
        icon: ListFilter,
        messageStart: () => "Changing the tables list...",
        messageEnd: () => "Changed the tables list",
        messageError: "Could not change the tables list",
      },
    },
  ];
}

// Mounted by the Tables page.
export function useTablesListTools(
  deps: TablesListDeps,
  enabled: boolean
): void {
  useGenieTools(() => buildTablesListTools(deps), [deps], enabled);
}
